import React, {useEffect} from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "../src/layouts/MainLayout/Layout";
import { ROUTES } from "./constants/routes";
import theme from "./Theme";

import Dashboard from "./pages/Dashboard/Dashboard";
import MapMonitoring from "./pages/MapMonitoring/MapMonitoring";
import Planning from "./pages/Planning/Planning";
import Handbook from "./pages/Handbook/Handbook";
import Reporting from "./pages/Reporting/Reporting";
import Admin from "./pages/Admin/Admin";
import CreateUser from "./pages/Admin/User/CreateUser/CreateUser";
import UpdateUser from "./pages/Admin/User/UpdateUser/UpdateUser";
import CreatePosition from "./pages/Admin/Position/CreatePosition/CreatePosition";
import UpdatePosition from "./pages/Admin/Position/UpdatePosition/UpdatePosition";
import CreateRight from "./pages/Admin/Right/CreateRight/CreateRight";
import UpdateRightView from "./pages/Admin/Right/UpdateRight/UpdateRightView";

import { ThemeProvider } from "@mui/material";
import { PrivateRoute } from "./components/PrivateRoute/PrivateRoute";
import { Login } from "./pages/Login/Login";
import { AuthProvider } from "./context/AuthContext";
import { RightsArray } from "./types/api/authorization/Rights";
import { OrganizationsTable } from "./pages/Handbook/_components/tables/OrganizationsTable/OrganizationsTable";
import { ContractorsTable } from "./pages/Handbook/_components/tables/ContractorsTable/ContractorsTable";
import { ContainerSitesTable } from "./pages/Handbook/_components/tables/ContainerSitesTable/ContainerSitesTable";
import {ContainerSitesTypesTable} from "./pages/Handbook/_components/tables/ContainerSitesTypesTable/ContainerSitesTypesTable";
import { GeozonesTable } from "./pages/Handbook/_components/tables/GeozonesTable/GeozonesTable";
import { ContainersTable } from "./pages/Handbook/_components/tables/ContainersTable/ContainersTable";
import { ContainerTypesTable } from "./pages/Handbook/_components/tables/ContainerTypesTable/ContainerTypesTable";
import { VehiclesTable } from "./pages/Handbook/_components/tables/VehiclesTable/VehiclesTable";
import { VehicleStatusesTable } from "./pages/Handbook/_components/tables/VehicleStatusesTable/VehicleStatusesTable";
import { WasteTypesTable } from "./pages/Handbook/_components/tables/WasteTypesTable/WasteTypesTable";
import { LoadTypesTable } from "./pages/Handbook/_components/tables/LoadTypesTable/LoadTypesTable";
import { GroupGeofenceTable } from "./pages/Handbook/_components/tables/GroupGeofenceTable/GroupGeofenceTable";

import { HandbookTableWrapper } from "./pages/Handbook/HandbookTableWrapper";
import {disconnect, initSignalR} from "./signalR/signalR";

function App() {

    useEffect(() => {
        initSignalR().catch(console.error);

        return () => {
            disconnect().catch(() => {});
        };
    }, []);

    return (
        <AuthProvider>
            <ThemeProvider theme={theme}>
                <BrowserRouter>
                    <Routes>
                        <Route path={ROUTES.LOGIN.path} element={<Login />} />

                        <Route
                            path={ROUTES.HOME.path}
                            element={
                                <PrivateRoute permissions={RightsArray}>
                                    <Layout />
                                </PrivateRoute>
                            }
                        >
                            <Route
                                index
                                element={
                                    <PrivateRoute permissions={["CheckMap"]}>
                                        <MapMonitoring />
                                    </PrivateRoute>
                                }
                            />
                            <Route
                                path={ROUTES.DASHBOARD.path}
                                element={
                                    <PrivateRoute permissions={["CheckDashboard"]}>
                                        <Dashboard />
                                    </PrivateRoute>
                                }
                            />

                            <Route
                                path={ROUTES.MAP.path}
                                element={
                                    <PrivateRoute permissions={["CheckMap"]}>
                                        <MapMonitoring />
                                    </PrivateRoute>
                                }
                            />

                            <Route
                                path={ROUTES.PLANNING.path}
                                element={
                                    <PrivateRoute permissions={["CheckPlan"]}>
                                        <Planning />
                                    </PrivateRoute>
                                }
                            />

                            {/* ✅ Handbook + nested routes */}
                            <Route
                                path={ROUTES.HANDBOOK.path}
                                element={
                                    <PrivateRoute permissions={["CheckGuide"]}>
                                        <Handbook />
                                    </PrivateRoute>
                                }
                            >
                                {/* /handbook -> редирект на organizations */}
                                <Route
                                    index
                                    element={<Navigate to={ROUTES.HANDBOOK.ORGANIZATIONS} replace />}
                                />

                                <Route
                                    path={ROUTES.HANDBOOK.ORGANIZATIONS}
                                    element={
                                        <HandbookTableWrapper>
                                            {(context) => <OrganizationsTable {...context} />}
                                        </HandbookTableWrapper>
                                    }
                                />

                                <Route
                                    path={ROUTES.HANDBOOK.CONTRACTORS}
                                    element={
                                        <HandbookTableWrapper>
                                            {(context) => <ContractorsTable {...context} />}
                                        </HandbookTableWrapper>
                                    }
                                />
                                <Route
                                    path={ROUTES.HANDBOOK.SCHEDULE}
                                    element={
                                        <>
                                            {/* Schedule moved to Reporting */}
                                            <Navigate
                                                to={`${ROUTES.REPORT.path}?sheet=schedule`}
                                                replace
                                            />
                                        </>
                                    }
                                />

                                <Route
                                    path={ROUTES.HANDBOOK.CONTAINER_SITES}
                                    element={
                                        <HandbookTableWrapper>
                                            {(context) => <ContainerSitesTable {...context} />}
                                        </HandbookTableWrapper>
                                    }
                                />

                                <Route
                                    path={ROUTES.HANDBOOK.CONTAINER_SITES_TYPES}
                                    element={
                                        <HandbookTableWrapper>
                                            {(context) => <ContainerSitesTypesTable {...context} />}
                                        </HandbookTableWrapper>
                                    }
                                />

                                <Route
                                    path={ROUTES.HANDBOOK.GEOZONES}
                                    element={
                                        <HandbookTableWrapper>
                                            {(context) => <GeozonesTable {...context} />}
                                        </HandbookTableWrapper>
                                    }
                                />

                                <Route
                                    path={ROUTES.HANDBOOK.CONTAINERS}
                                    element={
                                        <HandbookTableWrapper>
                                            {(context) => <ContainersTable {...context} />}
                                        </HandbookTableWrapper>
                                    }
                                />

                                <Route
                                    path={ROUTES.HANDBOOK.CONTAINER_TYPES}
                                    element={
                                        <HandbookTableWrapper>
                                            {(context) => <ContainerTypesTable {...context} />}
                                        </HandbookTableWrapper>
                                    }
                                />

                                <Route
                                    path={ROUTES.HANDBOOK.VEHICLES}
                                    element={
                                        <HandbookTableWrapper>
                                            {(context) => <VehiclesTable {...context} />}
                                        </HandbookTableWrapper>
                                    }
                                />

                                <Route
                                    path={ROUTES.HANDBOOK.VEHICLE_STATUSES}
                                    element={
                                        <HandbookTableWrapper>
                                            {(context) => (
                                                <VehicleStatusesTable {...context} />
                                            )}
                                        </HandbookTableWrapper>
                                    }
                                />
                                <Route
                                    path={ROUTES.HANDBOOK.LOAD_TYPES}
                                    element={
                                        <HandbookTableWrapper>
                                            {(context) => <LoadTypesTable {...context} />}
                                        </HandbookTableWrapper>
                                    }
                                />

                                <Route
                                    path={ROUTES.HANDBOOK.GROUP_GEOFENCE}
                                    element={
                                        <HandbookTableWrapper>
                                            {(context) => <GroupGeofenceTable {...context} />}
                                        </HandbookTableWrapper>
                                    }
                                />
                                <Route
                                    path={ROUTES.HANDBOOK.WASTE_TYPES}
                                    element={
                                        <HandbookTableWrapper>
                                            {(context) => <WasteTypesTable {...context} />}
                                        </HandbookTableWrapper>
                                    }
                                />
                            </Route>

                            <Route
                                path={ROUTES.REPORT.path}
                                element={
                                    <PrivateRoute permissions={["CheckReport"]}>
                                        <Reporting />
                                    </PrivateRoute>
                                }
                            />

                            <Route
                                path={ROUTES.ADMIN.path}
                                element={
                                    <PrivateRoute permissions={["AdminRights"]}>
                                        <Admin />
                                    </PrivateRoute>
                                }
                            />

                            <Route path={ROUTES.ADMIN_USERS.create} element={<CreateUser />} />
                            <Route path={ROUTES.ADMIN_USERS.update} element={<UpdateUser />} />

                            <Route
                                path={ROUTES.ADMIN_POSITIONS.create}
                                element={<CreatePosition />}
                            />
                            <Route
                                path={ROUTES.ADMIN_POSITIONS.update}
                                element={<UpdatePosition />}
                            />

                            <Route path={ROUTES.ADMIN_ROLES.create} element={<CreateRight />} />
                            <Route
                                path={ROUTES.ADMIN_ROLES.update}
                                element={<UpdateRightView />}
                            />
                        </Route>
                    </Routes>
                </BrowserRouter>
            </ThemeProvider>
        </AuthProvider>
    );
}

export default App;
